// The Modeled (or Modeled-Adjusted) FTE matrix: per (Discipline × Channel) FTE
// distribution across level groups (exact levels while adjusting, since
// overrides are stored per level), each column split into "On" (onshore) and
// "Hub" (nearshore + offshore — both are OGS hub roles per the model's shore
// tagging). The Hub sub-column is subtly tinted so the eye can scan onshore vs
// hub at a glance for any level. The right side keeps the row-level rollups
// (Total / Onshore / Hub / Hub %).
//
// The On/Hub split for every cell comes straight from the engine's per-cell
// shore split (`onshoreFte` / `nearshoreFte` / `offshoreFte`), which is driven
// by the scenario's per-discipline OGS configuration (enable, hub, %, and the
// selected levels), so the grid stays in sync with the scenario inputs rather
// than re-deriving from the model tables.
//
// Adjust mode layers manual per-cell edits on top, stored on the scenario as
// `profile.staffPlanOverrides`. That is deliberately not component state: the
// card is mounted on both the Staff Plan tab and the Summary page, and sharing
// the scenario field is what keeps the two showing the same numbers without any
// syncing code between them.

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Check, Pencil, RotateCcw } from 'lucide-react';
import {
  LEVEL_IDS,
  levelGroupOf,
  applyStaffPlanOverrides,
  basisCellsToStaffPlanCells,
  staffPlanCellKey,
  type StaffPlanCellDrift,
} from '@rc/engine';
import type {
  CalculationResult,
  ClientProfile,
  LevelId,
  Model,
  StaffPlanCellOverride,
} from '@rc/engine/types';
import { basisAfterAdjustment } from './automationBasis';
import PublishPlanButton from './PublishPlanButton';
import type { FindPublishedPlan, PublishPlanArgs } from './publishPlan';
import {
  buildGroups,
  columnSplit,
  GROUP_COLUMNS,
  LEVEL_COLUMNS,
  levelTotal,
  MATRIX_EPS,
  type DisciplineGroup,
  type LevelSplit,
  type MatrixColumn,
} from './staffMatrix';

interface Props {
  model: Model;
  result: CalculationResult;
  profile: ClientProfile;
  onProfileChange?: (patch: Partial<ClientProfile>) => void;
  /** Publish the matrix to the client workspace as a saved staff plan. The
   *  caller owns naming provenance and refreshing its own plan list. */
  onPublishPlan?: (args: PublishPlanArgs) => Promise<void>;
  /** Resolves the plan a re-save would update in place, when there is one. */
  findPublishedPlan?: FindPublishedPlan;
  /** Seed for the publish name field, e.g. the scenario's name. */
  defaultPlanName?: string;
  /** Small label above the heading. The Summary page passes "Pre-Automation
   *  Release" to place the grid in the chain that ends at the Final plan. */
  eyebrow?: string;
  /** Extra line under the description, for a caller that knows something about
   *  the surrounding page the card cannot see for itself. */
  notice?: React.ReactNode;
}

const EPS = MATRIX_EPS;

/** Half the grid's display precision. An edit that rounds to the modeled number
 *  is treated as no change, so tapping through cells cannot litter the scenario
 *  with overrides that say nothing. */
const SNAP = 0.05;

function round1(n: number): number {
  return Math.round((Number(n) || 0) * 10) / 10;
}

function fmt(n: number): string {
  if (!isFinite(n) || Math.abs(n) < 0.05) return '·';
  if (Math.abs(n) >= 100) return Math.round(n).toLocaleString();
  return n.toFixed(1);
}

function pct(num: number, den: number): string {
  if (!isFinite(num) || !isFinite(den) || den <= EPS) return '·';
  return `${Math.round((num / den) * 100)}%`;
}

// Subtle column-pair styling. The "On" sub-column gets a thin slate divider on
// its left edge to visually group each level pair, and the "Hub" sub-column
// gets a faint warm tint + slightly muted text to differentiate it from the
// onshore sub-column without shouting.
const ON_BASE = 'py-1.5 pl-2 pr-1 text-right border-l border-slate-100';
const HUB_BASE = 'py-1.5 pl-1 pr-2 text-right bg-amber-50/40';

export default function ModeledMatrixCard({
  model,
  result,
  profile,
  onProfileChange,
  onPublishPlan,
  findPublishedPlan,
  defaultPlanName,
  eyebrow,
  notice,
}: Props) {
  const [adjustMode, setAdjustMode] = useState(false);
  const modeledCells = result.byDisciplineChannelLevel;
  const overrides = profile.staffPlanOverrides;
  // Manual edits are layered on after the engine, so the modeled grid stays the
  // stored answer for the scenario and Modeled-Adjusted is derived on render.
  const adjusted = useMemo(
    () => applyStaffPlanOverrides(modeledCells, overrides, model),
    [modeledCells, overrides, model],
  );
  const cells = adjusted.cells;
  const isAdjusted = adjusted.appliedCount > 0 || adjusted.orphanedKeys.length > 0;
  const driftedKeys = useMemo(
    () => new Set(adjusted.drifted.map((d) => d.key)),
    [adjusted.drifted],
  );

  /** The modeled On/Hub for each cell, which every edit is measured against. */
  const modeledByKey = useMemo(() => {
    const m = new Map<string, LevelSplit>();
    for (const c of modeledCells || []) {
      m.set(staffPlanCellKey(c.disciplineId, c.channelId, c.levelId), {
        on: Number(c.onshoreFte) || 0,
        hub: (Number(c.nearshoreFte) || 0) + (Number(c.offshoreFte) || 0),
      });
    }
    return m;
  }, [modeledCells]);

  const groups = useMemo(
    () =>
      buildGroups(
        cells,
        (model.disciplines || [])
          .filter((d) => d.active !== false)
          .map((d) => ({ id: d.id, label: d.label })),
        (model.channels || []).map((c) => ({ id: c.id, label: c.label })),
        modeledCells,
      ),
    [model, cells, modeledCells],
  );

  // Level groups when reading; exact levels while adjusting, since overrides
  // are stored per level. Columns are drawn from the modeled grid as well as
  // the adjusted one, so a column cannot disappear mid-edit and strand the
  // input that would undo it.
  const activeColumns = useMemo(() => {
    const live = new Set<LevelId>();
    for (const c of modeledCells || []) {
      if ((Number(c.adjustedFte) || 0) > EPS) live.add(c.levelId);
    }
    return (adjustMode ? LEVEL_COLUMNS : GROUP_COLUMNS).filter(
      (col) =>
        col.levelIds.some((l) => live.has(l)) ||
        groups.some((g) => levelTotal(columnSplit(g.byLevel, col)) > EPS),
    );
  }, [adjustMode, groups, modeledCells]);

  const setOverrides = (next: Record<string, StaffPlanCellOverride>) => {
    const any = Object.keys(next).length > 0;
    const patch: Partial<ClientProfile> = {
      staffPlanOverrides: any ? next : undefined,
    };
    const basis = basisAfterAdjustment(
      profile.automationBasis,
      Object.keys(overrides || {}).length > 0,
      any,
    );
    if (basis) patch.automationBasis = basis;
    onProfileChange?.(patch);
  };

  const setCell = (key: string, side: 'on' | 'hub', value: number) => {
    const modeled = modeledByKey.get(key) || { on: 0, hub: 0 };
    const cur = overrides?.[key];
    const nextOn = round1(side === 'on' ? value : (cur?.onFte ?? modeled.on));
    const nextHub = round1(side === 'hub' ? value : (cur?.hubFte ?? modeled.hub));

    const next = { ...(overrides || {}) };
    if (
      Math.abs(nextOn - modeled.on) < SNAP &&
      Math.abs(nextHub - modeled.hub) < SNAP
    ) {
      // Typing the modeled number back in is a revert, not a no-op override.
      // The tolerance is half the grid's display precision, so a value that
      // rounds to what the model says counts as unchanged.
      delete next[key];
    } else {
      // Baselines are re-stamped on every edit: adjusting a cell now means
      // acknowledging what the model currently says about it.
      next[key] = {
        onFte: nextOn,
        hubFte: nextHub,
        baselineOnFte: modeled.on,
        baselineHubFte: modeled.hub,
        at: new Date().toISOString(),
      };
    }
    setOverrides(next);
  };

  const revertCell = (key: string) => {
    if (!overrides?.[key]) return;
    const next = { ...overrides };
    delete next[key];
    setOverrides(next);
  };

  const clearAdjustments = () => onProfileChange?.({ staffPlanOverrides: undefined });

  /** Accept the model's new numbers as the reference point while keeping every
   *  figure the user typed. This is the answer to "the model moved but I still
   *  want my headcount". */
  const rebaselineDrifted = () => {
    if (!overrides) return;
    const next = { ...overrides };
    for (const d of adjusted.drifted) {
      const cur = next[d.key];
      if (!cur) continue;
      next[d.key] = {
        ...cur,
        baselineOnFte: d.modeledOnFte,
        baselineHubFte: d.modeledHubFte,
      };
    }
    setOverrides(next);
  };

  /** Give up the manual figures on the cells the model has moved, leaving the
   *  rest of the adjustments in place. */
  const takeModeledForDrifted = () => {
    if (!overrides) return;
    const next = { ...overrides };
    for (const d of adjusted.drifted) delete next[d.key];
    setOverrides(next);
  };

  if (!cells || cells.length === 0 || groups.length === 0) {
    return (
      <section className="card">
        <div className="card-body text-sm text-slate-500">
          No discipline × channel × level data available for this scenario yet.
        </div>
      </section>
    );
  }

  const grand = groups.reduce(
    (acc, g) => {
      const next: typeof acc = {
        total: acc.total + g.total,
        onshore: acc.onshore + g.onshore,
        hub: acc.hub + g.hub,
        byLevel: { ...acc.byLevel },
      };
      for (const lv of LEVEL_IDS) {
        const cur = next.byLevel[lv] ?? { on: 0, hub: 0 };
        const add = g.byLevel[lv] ?? { on: 0, hub: 0 };
        next.byLevel[lv] = { on: cur.on + add.on, hub: cur.hub + add.hub };
      }
      return next;
    },
    {
      total: 0,
      onshore: 0,
      hub: 0,
      byLevel: {} as Partial<Record<LevelId, LevelSplit>>,
    },
  );

  return (
    <section className="card">
      <div className="card-header">
        <div>
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
              {eyebrow}
            </div>
          )}
          <h3 className="text-base font-semibold">
            {isAdjusted ? 'Modeled-Adjusted' : 'Modeled'} FTE by Discipline ×
            Channel
          </h3>
          <p className="text-xs text-slate-500">
            Modeled FTE per (Discipline × Channel) split across job level
            groups (Adjust edits by exact level). The On / Hub split comes from
            the scenario's{' '}
            <span className="font-medium text-slate-600">OGS configuration</span>{' '}
            (per-discipline hub, % and selected roles). Hub sub-columns are
            tinted to make the comparison easy to scan.
          </p>
          {notice && <p className="text-xs text-slate-500 mt-1">{notice}</p>}
          {isAdjusted && (
            <p className="text-xs text-slate-500 mt-1">
              {adjusted.appliedCount} cell
              {adjusted.appliedCount === 1 ? '' : 's'} hand-adjusted. Adjusted
              headcount no longer follows the budget that sized it.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onPublishPlan && (
            <PublishPlanButton
              defaultName={`${defaultPlanName || 'Scenario'}${isAdjusted ? ' — Modeled-Adjusted' : ' — Modeled'}`}
              note={
                <>
                  Saved to this client's staff plans, tagged as generated from{' '}
                  {isAdjusted ? 'a model and hand-adjusted' : 'a model'}.
                  Generated plans cannot be attached to a scenario as its Actual
                  team, so hub and OGS savings keep measuring against the real
                  one.
                </>
              }
              existing={findPublishedPlan?.('modeled')}
              onPublish={(name, replaceId) =>
                onPublishPlan({
                  name,
                  kind: 'modeled',
                  cells: basisCellsToStaffPlanCells(cells),
                  source: { adjustedCellCount: adjusted.appliedCount },
                  replaceId,
                })
              }
            />
          )}
          {onProfileChange && (
            <>
              {isAdjusted && (
                <button
                  type="button"
                  onClick={clearAdjustments}
                  className="btn-secondary text-xs"
                  title="Discard every manual edit and return to the modeled numbers"
                >
                  <RotateCcw size={13} /> Reset to modeled
                </button>
              )}
              <button
                type="button"
                onClick={() => setAdjustMode((v) => !v)}
                className={clsx(
                  'text-xs',
                  adjustMode ? 'btn-primary' : 'btn-secondary',
                )}
                aria-pressed={adjustMode}
                title="Hand-edit the modeled FTE for individual cells"
              >
                <Pencil size={13} /> {adjustMode ? 'Done adjusting' : 'Adjust'}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="card-body overflow-x-auto">
        {adjusted.drifted.length > 0 && (
          <DriftBanner
            drifted={adjusted.drifted}
            readOnly={!onProfileChange}
            onKeepMine={rebaselineDrifted}
            onTakeModeled={takeModeledForDrifted}
            onClearAll={clearAdjustments}
          />
        )}
        {adjusted.orphanedKeys.length > 0 && (
          <OrphanNotice
            count={adjusted.orphanedKeys.length}
            readOnly={!onProfileChange}
            onDrop={() => {
              if (!overrides) return;
              const next = { ...overrides };
              for (const k of adjusted.orphanedKeys) delete next[k];
              setOverrides(next);
            }}
          />
        )}
        <table className="w-full text-xs tabular-nums border-separate border-spacing-0">
          <thead>
            <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
              <th
                rowSpan={2}
                className="text-left align-bottom py-2 pr-3 font-medium border-b border-slate-200"
              >
                Discipline
              </th>
              <th
                rowSpan={2}
                className="text-left align-bottom py-2 pr-3 font-medium border-b border-slate-200"
              >
                Channel
              </th>
              {activeColumns.map((col) => (
                <th
                  key={col.key}
                  colSpan={2}
                  className="text-center pt-2 pb-1 px-1 font-semibold text-slate-600 border-b border-slate-100 whitespace-nowrap normal-case tracking-normal"
                  title={col.label}
                >
                  {col.levelId ?? col.label}
                </th>
              ))}
              <th
                rowSpan={2}
                className="text-right align-bottom py-2 pl-3 pr-2 font-medium border-b border-slate-200"
              >
                Total FTE
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom py-2 px-2 font-medium border-b border-slate-200"
              >
                Onshore
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom py-2 px-2 font-medium border-b border-slate-200 bg-amber-50/40"
              >
                Hub
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom py-2 pl-2 font-medium border-b border-slate-200"
              >
                Hub %
              </th>
            </tr>
            <tr className="text-[9px] tracking-wider text-slate-400">
              {activeColumns.map((col) => (
                <Fragmentish key={col.key}>
                  <th className="font-medium text-right pb-2 pl-2 pr-1 border-b border-slate-200 border-l border-slate-100">
                    On
                  </th>
                  <th className="font-medium text-right pb-2 pl-1 pr-2 border-b border-slate-200 bg-amber-50/40 text-amber-700/70">
                    Hub
                  </th>
                </Fragmentish>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <DisciplineBlock
                key={g.disciplineId}
                group={g}
                columns={activeColumns}
                isLast={gi === groups.length - 1}
                adjustMode={adjustMode}
                overrides={overrides}
                driftedKeys={driftedKeys}
                onCellChange={setCell}
                onCellRevert={revertCell}
              />
            ))}
            <tr className="bg-brand-purple-50/40">
              <td
                className="py-2 pr-3 font-semibold text-brand-purple-800 border-t-2 border-slate-300"
                colSpan={2}
              >
                Total
              </td>
              {activeColumns.map((col) => {
                const s = columnSplit(grand.byLevel, col);
                return (
                  <Fragmentish key={col.key}>
                    <td
                      className={clsx(
                        ON_BASE,
                        'border-t-2 border-slate-300 text-brand-purple-800 font-semibold',
                      )}
                    >
                      {fmt(s.on)}
                    </td>
                    <td
                      className={clsx(
                        HUB_BASE,
                        'border-t-2 border-slate-300 text-amber-800 font-semibold',
                      )}
                    >
                      {fmt(s.hub)}
                    </td>
                  </Fragmentish>
                );
              })}
              <td className="py-2 pl-3 pr-2 text-right text-brand-purple-800 font-semibold border-t-2 border-slate-300">
                {fmt(grand.total)}
              </td>
              <td className="py-2 px-2 text-right text-brand-purple-800 font-semibold border-t-2 border-slate-300">
                {fmt(grand.onshore)}
              </td>
              <td className="py-2 px-2 text-right text-amber-800 font-semibold border-t-2 border-slate-300 bg-amber-50/60">
                {fmt(grand.hub)}
              </td>
              <td className="py-2 pl-2 text-right text-brand-purple-800 font-semibold border-t-2 border-slate-300">
                {pct(grand.hub, grand.total)}
              </td>
            </tr>
          </tbody>
        </table>
        <Legend />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// React fragment helper that keeps adjacent <th>/<td> siblings clean inside the
// generated row without forcing us to spread an array.
function Fragmentish({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function Legend() {
  return (
    <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-wider text-slate-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm border border-slate-200 bg-white" />
        On — onshore FTE
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm border border-amber-200 bg-amber-50/60" />
        Hub — nearshore + offshore FTE
      </span>
    </div>
  );
}

/** Shown when the model has re-sized a cell the user had pinned. The pinned
 *  figure still wins — drift is surfaced, never silently resolved — so the user
 *  chooses whether the new modeled number changes their mind. */
function DriftBanner({
  drifted,
  readOnly,
  onKeepMine,
  onTakeModeled,
  onClearAll,
}: {
  drifted: StaffPlanCellDrift[];
  readOnly: boolean;
  onKeepMine: () => void;
  onTakeModeled: () => void;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const shown = open ? drifted : drifted.slice(0, 3);

  return (
    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-amber-900">
            The model has moved under {drifted.length} adjusted cell
            {drifted.length === 1 ? '' : 's'}
          </div>
          <p className="text-[11px] text-amber-800/90 mt-0.5">
            Your figures are still in force. The modeled numbers these cells were
            adjusted against have since changed — most often because budget,
            leveling or channel activation moved.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {shown.map((d) => (
              <li key={d.key} className="text-[11px] text-amber-900/90">
                <span className="font-medium">
                  {d.disciplineLabel} · {d.channelLabel} · {levelGroupOf(d.levelId).label} ·{' '}
                  {d.levelId}
                </span>
                <span className="text-amber-800/80">
                  {' '}
                  — model now {fmtSplit(d.modeledOnFte, d.modeledHubFte)}, was{' '}
                  {fmtSplit(d.baselineOnFte, d.baselineHubFte)}, yours{' '}
                  {fmtSplit(d.onFte, d.hubFte)}
                </span>
              </li>
            ))}
          </ul>
          {drifted.length > 3 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1 text-[11px] text-amber-800 underline hover:text-amber-950"
            >
              {open ? 'Show fewer' : `Show all ${drifted.length}`}
            </button>
          )}
          {!readOnly && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onKeepMine}
                className="btn-primary text-xs"
                title="Accept the new modeled numbers as the reference point and keep the figures you typed"
              >
                <Check size={13} /> Keep my numbers
              </button>
              <button
                type="button"
                onClick={onTakeModeled}
                className="btn-secondary text-xs"
                title="Drop the manual figures on just these cells"
              >
                Take modeled for these cells
              </button>
              <button
                type="button"
                onClick={onClearAll}
                className="text-xs text-amber-800 underline hover:text-amber-950"
              >
                Clear all adjustments
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtSplit(on: number, hub: number): string {
  return `${on.toFixed(1)} on / ${hub.toFixed(1)} hub`;
}

/** Overrides whose cell the model no longer sizes at all. They still count
 *  toward the adjusted totals, but they cannot be edited in the grid because
 *  there is no row to edit, so the only honest options are to keep or drop. */
function OrphanNotice({
  count,
  readOnly,
  onDrop,
}: {
  count: number;
  readOnly: boolean;
  onDrop: () => void;
}) {
  return (
    <div className="mb-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 flex items-start gap-2">
      <AlertTriangle size={14} className="text-slate-500 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-slate-700">
          {count} adjustment{count === 1 ? '' : 's'} no longer match a modeled
          cell
        </div>
        <p className="text-[11px] text-slate-600 mt-0.5">
          The model has stopped sizing{' '}
          {count === 1 ? 'this discipline' : 'these disciplines'} × channel ×
          level, so {count === 1 ? 'it is' : 'they are'} not shown in the grid
          below. The headcount still counts toward the adjusted totals.
        </p>
        {!readOnly && (
          <button
            type="button"
            onClick={onDrop}
            className="mt-1.5 text-[11px] text-brand-purple-600 underline hover:text-brand-purple-800"
          >
            Drop {count === 1 ? 'it' : 'them'}
          </button>
        )}
      </div>
    </div>
  );
}

function adjustedTitle(ov: StaffPlanCellOverride, side: 'on' | 'hub'): string {
  const mine = side === 'on' ? ov.onFte : ov.hubFte;
  const modeled = side === 'on' ? ov.baselineOnFte : ov.baselineHubFte;
  return `Hand-adjusted to ${mine.toFixed(1)}; the model sized ${modeled.toFixed(1)}`;
}

/** One editable On or Hub figure. Overridden cells carry a purple ring, and a
 *  drifted cell an amber one, so the state of a number is legible without
 *  hovering. The revert control sits inline rather than floating over the
 *  input, which keeps it reliably clickable in a dense grid. */
function SplitInput({
  value,
  hub,
  overridden,
  drifted,
  onChange,
  onRevert,
}: {
  value: number;
  hub?: boolean;
  overridden: boolean;
  drifted: boolean;
  onChange: (n: number) => void;
  onRevert?: () => void;
}) {
  return (
    <div className="flex items-center gap-1 justify-end">
      {onRevert && (
        <button
          type="button"
          onClick={onRevert}
          className="text-brand-purple-500 hover:text-brand-purple-800 shrink-0"
          title="Revert this cell to the modeled figure"
          aria-label="Revert this cell to the modeled figure"
        >
          <RotateCcw size={10} />
        </button>
      )}
      <input
        type="number"
        min={0}
        step={0.1}
        className={clsx(
          'input py-0.5 px-1 w-[54px] text-right text-xs tabular-nums',
          hub && 'bg-amber-50/30 border-amber-200/60 focus:border-amber-400',
          overridden &&
            (drifted
              ? 'ring-1 ring-amber-400 border-amber-400'
              : 'ring-1 ring-brand-purple-300 border-brand-purple-300'),
        )}
        value={value > EPS ? round1(value) : ''}
        placeholder="—"
        onChange={(e) =>
          onChange(e.target.value === '' ? 0 : Number(e.target.value))
        }
      />
    </div>
  );
}

function DisciplineBlock({
  group,
  columns,
  isLast,
  adjustMode,
  overrides,
  driftedKeys,
  onCellChange,
  onCellRevert,
}: {
  group: DisciplineGroup;
  columns: MatrixColumn[];
  isLast: boolean;
  adjustMode: boolean;
  overrides?: Record<string, StaffPlanCellOverride>;
  driftedKeys: Set<string>;
  onCellChange: (key: string, side: 'on' | 'hub', value: number) => void;
  onCellRevert: (key: string) => void;
}) {
  const showSubtotal = group.rows.length > 1;
  return (
    <>
      {group.rows.map((row, ri) => (
        <tr
          key={`${group.disciplineId}::${row.channelId}`}
          className="hover:bg-slate-50/60"
        >
          <td
            className={clsx(
              'py-1.5 pr-3 text-slate-700 font-medium whitespace-nowrap border-t',
              ri === 0 ? 'border-slate-200' : 'border-slate-100',
            )}
          >
            {ri === 0 ? group.disciplineLabel : ''}
          </td>
          <td
            className={clsx(
              'py-1.5 pr-3 text-slate-600 whitespace-nowrap border-t',
              ri === 0 ? 'border-slate-200' : 'border-slate-100',
            )}
          >
            {row.channelLabel}
          </td>
          {columns.map((col) => {
            const s = columnSplit(row.byLevel, col);
            const keys = col.levelIds.map((l) =>
              staffPlanCellKey(group.disciplineId, row.channelId, l),
            );
            const key = keys[0];
            const ov = col.levelId ? overrides?.[key] : undefined;
            const anyOverridden = keys.some((k) => overrides?.[k]);
            const topBorder = ri === 0 ? 'border-t-slate-200' : 'border-t-slate-100';
            if (adjustMode && col.levelId) {
              return (
                <Fragmentish key={col.key}>
                  <td className={clsx(ON_BASE, 'border-t', topBorder)}>
                    <SplitInput
                      value={s.on}
                      overridden={!!ov}
                      drifted={driftedKeys.has(key)}
                      onChange={(n) => onCellChange(key, 'on', n)}
                      onRevert={ov ? () => onCellRevert(key) : undefined}
                    />
                  </td>
                  <td className={clsx(HUB_BASE, 'border-t', topBorder)}>
                    <SplitInput
                      value={s.hub}
                      hub
                      overridden={!!ov}
                      drifted={driftedKeys.has(key)}
                      onChange={(n) => onCellChange(key, 'hub', n)}
                    />
                  </td>
                </Fragmentish>
              );
            }
            const titleFor = (side: 'on' | 'hub') =>
              ov
                ? adjustedTitle(ov, side)
                : anyOverridden
                  ? 'Includes hand-adjusted levels'
                  : undefined;
            return (
              <Fragmentish key={col.key}>
                <td
                  className={clsx(
                    ON_BASE,
                    'text-slate-700 border-t',
                    topBorder,
                    anyOverridden && 'bg-brand-purple-50/60 font-semibold',
                  )}
                  title={titleFor('on')}
                >
                  {fmt(s.on)}
                </td>
                <td
                  className={clsx(
                    HUB_BASE,
                    'text-amber-900/80 border-t',
                    topBorder,
                    anyOverridden && 'bg-brand-purple-50/60 font-semibold',
                  )}
                  title={titleFor('hub')}
                >
                  {fmt(s.hub)}
                </td>
              </Fragmentish>
            );
          })}
          <td
            className={clsx(
              'py-1.5 pl-3 pr-2 text-right text-slate-800 font-medium border-t',
              ri === 0 ? 'border-slate-200' : 'border-slate-100',
            )}
          >
            {fmt(row.total)}
          </td>
          <td
            className={clsx(
              'py-1.5 px-2 text-right text-slate-700 border-t',
              ri === 0 ? 'border-slate-200' : 'border-slate-100',
            )}
          >
            {fmt(row.onshore)}
          </td>
          <td
            className={clsx(
              'py-1.5 px-2 text-right text-amber-900/80 border-t bg-amber-50/40',
              ri === 0 ? 'border-t-slate-200' : 'border-t-slate-100',
            )}
          >
            {fmt(row.hub)}
          </td>
          <td
            className={clsx(
              'py-1.5 pl-2 text-right text-slate-500 border-t',
              ri === 0 ? 'border-slate-200' : 'border-slate-100',
            )}
          >
            {pct(row.hub, row.total)}
          </td>
        </tr>
      ))}
      {showSubtotal && (
        <tr
          className={clsx(
            'bg-slate-50/60',
            isLast && 'border-b border-slate-200',
          )}
        >
          <td className="py-1.5 pr-3 text-slate-700 font-semibold whitespace-nowrap border-t border-slate-200">
            {group.disciplineLabel}
          </td>
          <td className="py-1.5 pr-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-t border-slate-200">
            Subtotal
          </td>
          {columns.map((col) => {
            const s = columnSplit(group.byLevel, col);
            return (
              <Fragmentish key={col.key}>
                <td className={clsx(ON_BASE, 'text-slate-700 font-semibold border-t border-slate-200')}>
                  {fmt(s.on)}
                </td>
                <td className={clsx(HUB_BASE, 'text-amber-900 font-semibold border-t border-slate-200')}>
                  {fmt(s.hub)}
                </td>
              </Fragmentish>
            );
          })}
          <td className="py-1.5 pl-3 pr-2 text-right text-slate-800 font-semibold border-t border-slate-200">
            {fmt(group.total)}
          </td>
          <td className="py-1.5 px-2 text-right text-slate-700 font-semibold border-t border-slate-200">
            {fmt(group.onshore)}
          </td>
          <td className="py-1.5 px-2 text-right text-amber-900 font-semibold border-t border-slate-200 bg-amber-50/60">
            {fmt(group.hub)}
          </td>
          <td className="py-1.5 pl-2 text-right text-slate-500 font-semibold border-t border-slate-200">
            {pct(group.hub, group.total)}
          </td>
        </tr>
      )}
    </>
  );
}
